import * as React from "react";
import { Suspense } from "react";
import PageContainer from "../core/PageContainer";
import Breadcrumbs from "../core/Breadcrumbs";
import ModernCard from "../core/ModernCard";
import { CrystalBallProspect } from "../../stores";
import { ApproversList } from "../crystal_ball/ApproversList";
import { wrapComponentForErrors } from "../utility/ErrorHandler";
import Modal from "../core/Modal";
import Player from "../core/Player";
import {
  CRYSTAL_BALL_DEMO_VIDEO,
  CRYSTAL_BALL_DEMO_VIDEO_SD,
} from "../../constants";
import { normalizeUserContextData } from "../../libs/user_context";

const ProspectsTable = React.lazy(() =>
  import("../crystal_ball/ProspectsTable")
);
const CrystalBallNotifications = React.lazy(() =>
  import("../crystal_ball/CrystalBallNotifications")
);

interface IProps {
  userContext: IUserContextData;
  userProspects: IProspectRecord[];
  employeeProspects: IProspectRecord[];
  isApprover: boolean;
}

interface IState {
  userContext: IUserContextNormalized;
  userProspects?: CrystalBallProspect[];
  employeeProspects?: CrystalBallProspect[];
  loaded: boolean;
  isApprover?: boolean;
  demoModalOpen: boolean;
}

class ProspectIndex extends React.Component<IProps, IState> {
  state: Readonly<IState> = {
    loaded: false,
    isApprover: this.props.isApprover,
    demoModalOpen: false,
    userContext: normalizeUserContextData(this.props.userContext),
  };

  async componentDidMount() {
    const {
      CrystalBallProspect: CrystalBallProspectStore,
    } = await import("../../stores");

    const userProspects = this.props.userProspects.map(
      p => new CrystalBallProspectStore(p)
    );
    let employeeProspects;
    if (this.props.employeeProspects) {
      employeeProspects = this.props.employeeProspects.map(
        p => new CrystalBallProspectStore(p)
      );
    }
    this.setState({
      userProspects,
      employeeProspects,
      loaded: true,
    });
  }

  render() {
    const {
      userContext,
      userProspects,
      employeeProspects,
      loaded,
      isApprover,
      demoModalOpen,
    } = this.state;

    if (!loaded) {
      return null;
    }

    const currentUser = userContext.user;

    return (
      <PageContainer>
        <Breadcrumbs crumbs={[{ label: "Crystal Ball" }]} />
        <div className="level">
          <div className="level-left">
            {currentUser.isAdmin && (
              <div className={"level-item"}>
                <a
                  className="btn btn-theme-primary"
                  href={"/crystal_ball/admin"}
                >
                  <i className="fa fa-list" /> Admin
                </a>
              </div>
            )}
          </div>
          <div className="level-right">
            <div className="level-item">
              <button
                className="btn btn-theme-primary"
                onClick={() => this.setState({ demoModalOpen: true })}
              >
                <i className="fa fa-video-camera" /> Watch Demo
              </button>
            </div>
          </div>
        </div>
        <Suspense fallback={null}>
          <CrystalBallNotifications userContext={userContext!} />
        </Suspense>
        <Suspense fallback={null}>
          <ModernCard title="My Prospects" ta="left" m="0px">
            {userProspects!.length > 0 ? (
              <div>
                <a href={Routes.new_crystal_ball_prospect_path()}>
                  <button className="btn btn-theme-primary">
                    New Prospect
                  </button>
                </a>
                <hr />
                <ProspectsTable prospects={userProspects!} />
              </div>
            ) : (
              <div>
                <div className="columns is-centered">
                  <div className="column is-narrow">
                    No prospects have been started yet.
                  </div>
                </div>
                <div className="columns is-centered">
                  <div className="column is-narrow">
                    <a href={Routes.new_crystal_ball_prospect_path()}>
                      <button className="btn btn-theme-primary">
                        Start a Prospect
                      </button>
                    </a>
                  </div>
                </div>
              </div>
            )}
          </ModernCard>
          {employeeProspects && (
            <ModernCard title="Employee Prospects" ta="left" m="0px">
              <ProspectsTable prospects={employeeProspects} includeAuthor />
            </ModernCard>
          )}
          {(currentUser.isOwner || currentUser.isSalesManager || isApprover) && (
            <ModernCard title="Approvers" ta="left" m="0px">
              <ApproversList userContext={this.props.userContext} />
            </ModernCard>
          )}
        </Suspense>
        <Modal
          title={"Crystal Ball Demo"}
          open={demoModalOpen}
          close={() => this.setState({ demoModalOpen: false })}
          submittable={false}
          isLarge={true}
          onClose={() => {
            const player = window.jwplayer("crystal-ball-demo-player");
            if (player && player.getState() === "playing") {
              player.pause();
            }
          }}
        >
          <Player
            id={"crystal-ball-demo-player"}
            sources={[
              { file: CRYSTAL_BALL_DEMO_VIDEO, label: "HD" },
              { file: CRYSTAL_BALL_DEMO_VIDEO_SD, label: "SD" },
            ]}
          />
        </Modal>
      </PageContainer>
    );
  }
}

export default wrapComponentForErrors(ProspectIndex);



// WEBPACK FOOTER //
// ./app/javascript/components/pages/ProspectIndex.tsx